import { authAdapter } from '@/app/_auth/auth'
import { AuthStep } from '@/app/_auth/auth-step-navigator'
import { getKeycloak } from '@/app/_auth/keycloak-auth'
import { toast } from '@/components/ui/toaster'
import { usersApiPaths } from '@/hooks/api/_paths'
import { useApiRequest } from '@/lib/http-utils'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'

type EmergenceUserQueryResponse = {
  nextStep: AuthStep | null
  user: EmergenceUser | null
}

type EmergenceUserAPIResponse = {
  name: string
  email: string
  organization: string
  role: string
  accountStatus: 'ACTIVE' | 'DEACTIVATED' | 'CLOSED'
  createdAt: string
  lastUpdatedAt: string
  tier?: string
  welcome_message: boolean
}

function useEmergenceUserAndStepQuery(
  options: Omit<UseQueryOptions<EmergenceUserQueryResponse>, 'queryKey' | 'queryFn'> = {},
) {
  const { apiRequest } = useApiRequest()
  return useQuery<EmergenceUserQueryResponse>({
    queryKey: ['get-emergence-user'],
    queryFn: async () => {
      try {
        const response = await apiRequest({ path: usersApiPaths.base })

        if (response.status === 404) {
          return { nextStep: AuthStep.RegisterOrg, user: null }
        }

        if (response.status === 200) {
          const userData = (await response.json()) as EmergenceUserAPIResponse
          const emergenceUser = port(userData)

          if (emergenceUser.accountStatus === 'DEACTIVATED') {
            return { nextStep: AuthStep.Deactivated, user: emergenceUser }
          }

          return { nextStep: null, user: emergenceUser }
        }

        // Handle authentication errors (401/403) - these should be thrown so React Query treats them as errors
        // Don't sign out here - let the component handle it after error state is set
        if (response.status === 401 || response.status === 403) {
          const error = new Error('AUTHENTICATION_ERROR')
          error.name = 'AUTHENTICATION_ERROR'
          throw error
        }

        throw new Error('FAILED_TO_FETCH_EMERGENCE_USER_DATA')
      } catch (error) {
        // Check if this is an authentication-related error
        const isAuthError =
          (error instanceof Error &&
            (error.message === 'AUTHENTICATION_ERROR' ||
              error.message === 'KEYCLOAK_NOT_AUTHENTICATED' ||
              error.message === 'LOGGED_IN_USER_NOT_FOUND' ||
              error.message === 'FAILED_TO_GET_ACCESS_TOKEN' ||
              error.message === 'FAILED_TO_GET_KEYCLOAK_TOKEN')) ||
          (error instanceof Error && error.message.includes('AUTH'))

        if (isAuthError) {
          // Re-throw auth errors so React Query treats them as errors
          // Sign out has already been handled above for 401/403, but handle token errors here
          if (
            error instanceof Error &&
            (error.message === 'KEYCLOAK_NOT_AUTHENTICATED' ||
              error.message === 'LOGGED_IN_USER_NOT_FOUND' ||
              error.message === 'FAILED_TO_GET_ACCESS_TOKEN' ||
              error.message === 'FAILED_TO_GET_KEYCLOAK_TOKEN')
          ) {
            if (authAdapter) {
              await authAdapter.signOut().catch((err: unknown) => {
                console.error('Failed to sign out:', err)
              })
            }
            toast.error('Session expired. Please login again.')
          }
          throw error
        }

        // For other errors (network, etc.), sign out and throw so React Query treats it as an error
        if (authAdapter) {
          await authAdapter.signOut().catch((err: unknown) => {
            console.error('Failed to sign out:', err)
          })
        }
        toast.error('Error fetching account information. Please login again.')
        throw error
      }
    },
    ...options,
  })
}

type EmergenceUser = Omit<EmergenceUserAPIResponse, 'name'> & {
  uid: string
  tier: string
  firstName?: string
  lastName?: string
  fullName?: string
  photoURL?: string
}

function port({ name, tier, email, ...emUserData }: EmergenceUserAPIResponse): EmergenceUser {
  // Check if using Keycloak
  const keycloak = getKeycloak()
  if (keycloak) {
    return {
      uid: keycloak.tokenParsed?.sub ?? '',
      tier: tier ?? 'Free',
      email: email || keycloak.tokenParsed?.email || '',
      ...emUserData,
    }
  }

  // Firebase path
  const currentUser = authAdapter?.getCurrentUser()
  if (!email) throw new Error('EMAIL_NOT_FOUND')
  if (!currentUser?.uid) throw new Error('UID_NOT_FOUND')

  const emergenceUser: EmergenceUser = {
    uid: currentUser.uid,
    email,
    tier: tier ?? 'Free',
    ...emUserData,
  }

  const fullName = name ?? currentUser.displayName
  // format name
  if (fullName) {
    emergenceUser.fullName = fullName

    const nameArr = fullName.split(' ')
    const firstName = nameArr[0]

    emergenceUser.firstName = firstName

    if (nameArr.length > 1) {
      const lastName = nameArr.at(-1)

      emergenceUser.lastName = lastName
    }
  }
  // get photo url
  if (currentUser.photoURL) {
    emergenceUser.photoURL = currentUser.photoURL
  }

  return emergenceUser
}

export { useEmergenceUserAndStepQuery, type EmergenceUser }
